/**
 * Synthesise a deterministic demo receipt as a JPEG File. Drawn at
 * runtime via Canvas so we don't have to bundle any rights-cleared
 * receipt photo as a binary asset. The output is deliberately recognisable
 * as a demo (`DEMO RECEIPT` in the header, `Sample Café` as the vendor)
 * so a user clicking the empty-state link doesn't think they're seeing
 * a real receipt that leaked from somewhere.
 *
 * The image goes through the full processing pipeline like any other
 * upload — exercises perspective-correct (the receipt is axis-aligned
 * here so it short-circuits to no-op), preprocess, OCR, parse, validate.
 * Confirms the user can see the end-to-end flow before risking their
 * own data.
 */

const CANVAS_WIDTH = 560;
const CANVAS_HEIGHT = 1040;
const MARGIN_X = 48;
const LINE_HEIGHT = 30;

interface Line {
  text: string;
  font: string;
  align?: CanvasTextAlign;
  marginTop?: number;
}

// Layout: vendor as the first line so the parser's "first vendor-shaped
// line" rule lands on it cleanly. "DEMO RECEIPT" demoted to a smaller
// subtitle below — still visible so the user knows it's a demo without
// stealing the vendor slot.
//
// Body text bumped from 14px to 17px monospace. At 14px in JPEG q=0.92,
// Tesseract was confusing "8" with "3" on the items column (the sample
// "2 Orange Juice 8.00" came back as "3.00", breaking the items+tax≈total
// math). 17px's stroke width is well above the AA-noise floor — every
// digit reads correctly.
const LINES: Line[] = [
  { text: 'Sample Café', font: '700 32px Georgia, serif', align: 'center', marginTop: 30 },
  { text: '— DEMO RECEIPT —', font: '600 14px "Helvetica Neue", Arial, sans-serif', align: 'center', marginTop: 6 },
  { text: '123 Demo Street', font: '17px monospace', align: 'center', marginTop: 8 },
  { text: 'Springfield, 00000', font: '17px monospace', align: 'center' },
  { text: 'TEL: 555-DEMO', font: '17px monospace', align: 'center' },
  { text: '------------------------------', font: '17px monospace', align: 'center', marginTop: 12 },
  { text: 'Date: 15/01/2026', font: '17px monospace', marginTop: 8 },
  { text: 'Order #1234', font: '17px monospace' },
  { text: '------------------------------', font: '17px monospace', align: 'center', marginTop: 8 },
  { text: '2 Croissant           $7.00', font: '17px monospace', marginTop: 8 },
  { text: '1 Espresso            $3.50', font: '17px monospace' },
  { text: '1 Avocado Toast      $12.00', font: '17px monospace' },
  { text: '2 Orange Juice        $8.00', font: '17px monospace' },
  { text: '1 Pancakes           $12.00', font: '17px monospace' },
  { text: '------------------------------', font: '17px monospace', align: 'center', marginTop: 12 },
  { text: 'Subtotal             $42.50', font: '17px monospace', marginTop: 8 },
  { text: 'Tax                   $0.00', font: '17px monospace' },
  { text: 'TOTAL                $42.50', font: '700 19px monospace', marginTop: 6 },
  { text: '------------------------------', font: '17px monospace', align: 'center', marginTop: 12 },
  { text: 'Payment: Visa ****1234', font: '17px monospace', marginTop: 8 },
  { text: 'Thank you for visiting!', font: 'italic 17px Georgia, serif', align: 'center', marginTop: 30 },
];

export async function buildSampleReceiptFile(): Promise<File | null> {
  const canvas = document.createElement('canvas');
  canvas.width = CANVAS_WIDTH;
  canvas.height = CANVAS_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (ctx === null) return null;

  // Off-white paper background (exact pure white triggers Otsu corner cases
  // in scan-correct that real thermal paper doesn't).
  ctx.fillStyle = '#fafaf7';
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  ctx.fillStyle = '#111';
  let y = MARGIN_X;
  for (const line of LINES) {
    y += line.marginTop ?? 0;
    y += LINE_HEIGHT * 0.7;
    ctx.font = line.font;
    ctx.textAlign = line.align ?? 'left';
    const x =
      line.align === 'center'
        ? CANVAS_WIDTH / 2
        : line.align === 'right'
          ? CANVAS_WIDTH - MARGIN_X
          : MARGIN_X;
    ctx.fillText(line.text, x, y);
    y += LINE_HEIGHT * 0.3;
  }

  const blob: Blob | null = await new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b), 'image/jpeg', 0.92);
  });
  if (blob === null) return null;
  return new File([blob], 'sample-receipt.jpg', { type: 'image/jpeg' });
}
